import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * WebSocket che si riconnette da solo.
 *
 * PietroChip si usa in piedi, all'aperto, su reti che vanno e vengono: il
 * tablet che perde il campo per dieci secondi è la norma, non l'eccezione.
 * Quindi la connessione si riapre da sola, con attese crescenti, e ogni volta
 * che si riapre il server rimanda lo stato completo — così dopo un buco non
 * resta in giro nessuna informazione vecchia.
 *
 * Lo stato esposto è deliberatamente a tre valori e non booleano: "sto
 * provando a riconnettermi" è diverso da "non ci sono mai riuscito", e
 * l'interfaccia li mostra in modo diverso.
 */

export type SocketStatus = 'connecting' | 'open' | 'reconnecting';

/**
 * Dopo quanto silenzio considerare morta una connessione che sembrava aperta.
 *
 * Dodici secondi e non quarantacinque: quando il socket è bloccato dalla rete
 * (non chiuso — bloccato, che è il caso dei proxy) il client non lo sa, e per
 * tutto questo tempo lavora su dati vecchi. Il ricaricamento di riserva copre
 * solo quando lo stato non è più `open`, quindi è questa soglia a decidere
 * quanto a lungo si può restare ciechi.
 */
const SILENCE_TIMEOUT_MS = 12_000;
/**
 * Ogni quanto dichiararsi vivi quando non arriva altro traffico.
 *
 * Deve restare **sotto** la soglia di silenzio, altrimenti il watchdog
 * dichiarerebbe morto un socket sano e lo riaprirebbe a vuoto.
 */
const PING_INTERVAL_MS = 5_000;

const BACKOFF_START_MS = 800;
/**
 * Quanto si aspetta al massimo fra due tentativi.
 *
 * Quattro secondi e non dieci: con il ricaricamento di riserva a due secondi,
 * un'attesa di dieci renderebbe il ritorno al canale normale più lento del
 * ripiego che dovrebbe sostituire.
 */
const BACKOFF_MAX_MS = 4_000;

export type ReconnectingSocket = {
  status: SocketStatus;
  /** Numero di riaperture riuscite: cambia a ogni riconnessione. */
  generation: number;
  send: (message: unknown) => void;
};

export function useReconnectingSocket(
  url: string | null,
  onMessage: (data: unknown) => void,
): ReconnectingSocket {
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const [generation, setGeneration] = useState(0);

  const socketRef = useRef<WebSocket | null>(null);
  const timersRef = useRef<{ retry?: number; ping?: number; silence?: number }>({});
  const attemptsRef = useRef(0);
  const closedByUsRef = useRef(false);

  // Il gestore dei messaggi cambia a ogni render: tenerlo in una ref evita di
  // riaprire il socket (e perdere lo stato) ogni volta che un componente si
  // ridisegna.
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  const clearTimers = useCallback(() => {
    const timers = timersRef.current;
    if (timers.retry !== undefined) window.clearTimeout(timers.retry);
    if (timers.ping !== undefined) window.clearInterval(timers.ping);
    if (timers.silence !== undefined) window.clearTimeout(timers.silence);
    timersRef.current = {};
  }, []);

  const connect = useCallback(() => {
    if (!url) return;

    clearTimers();
    closedByUsRef.current = false;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      scheduleRetry();
      return;
    }

    socketRef.current = socket;

    /** Riarmo dell'attesa crescente: 0,8s, 1,6s, 3,2s… fino a 10s. */
    function scheduleRetry() {
      if (closedByUsRef.current) return;

      setStatus('reconnecting');

      const base = Math.min(BACKOFF_START_MS * 2 ** attemptsRef.current, BACKOFF_MAX_MS);
      // Un po' di casualità: dieci tablet che si riconnettono insieme dopo un
      // buco di rete non devono bussare tutti nello stesso istante.
      const delay = base * (0.75 + Math.random() * 0.5);
      attemptsRef.current += 1;

      timersRef.current.retry = window.setTimeout(connect, delay);
    }

    function armSilenceWatchdog() {
      if (timersRef.current.silence !== undefined) {
        window.clearTimeout(timersRef.current.silence);
      }
      timersRef.current.silence = window.setTimeout(() => {
        // Nessuna notizia per troppo tempo: il socket sembra aperto ma non lo
        // è più (succede quando la rete cambia senza chiudere niente).
        socket.close();
      }, SILENCE_TIMEOUT_MS);
    }

    socket.addEventListener('open', () => {
      attemptsRef.current = 0;
      setStatus('open');
      setGeneration((value) => value + 1);

      timersRef.current.ping = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL_MS);

      armSilenceWatchdog();
    });

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      armSilenceWatchdog();
      try {
        handlerRef.current(JSON.parse(event.data));
      } catch {
        // Un messaggio illeggibile non deve far cadere la connessione: il
        // prossimo `hello` rimette comunque tutto a posto.
      }
    });

    socket.addEventListener('close', () => {
      if (socketRef.current === socket) socketRef.current = null;
      scheduleRetry();
    });

    socket.addEventListener('error', () => {
      // `close` arriva comunque subito dopo: la riconnessione si gestisce lì.
    });
  }, [url, clearTimers]);

  useEffect(() => {
    if (!url) return;

    connect();

    return () => {
      closedByUsRef.current = true;
      clearTimers();
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [url, connect, clearTimers]);

  /**
   * Quando il sistema dichiara che la rete è tornata, si riprova subito invece
   * di aspettare il prossimo tentativo programmato: l'attesa crescente serve a
   * non tempestare un server irraggiungibile, non a punire chi ha appena
   * riagganciato il wifi.
   */
  useEffect(() => {
    function onOnline() {
      if (socketRef.current === null || socketRef.current.readyState > WebSocket.OPEN) {
        attemptsRef.current = 0;
        connect();
      }
    }

    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [connect]);

  const send = useCallback((message: unknown) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }, []);

  return { status, generation, send };
}
